/**
 * FinderlyFix admin → one organisation → Billing (contractor pages phase 2).
 * Embedded in the partner detail page (`/ops/partners/:id#billing`), and
 * linked from every row of `/ops/billing`.
 *
 *   * the account: model, lead fee, per-trade prices, success fee (bps),
 *     fees on/off — saved with a note;
 *   * the last 50 ledger entries, each reversible, and a form to post a
 *     credit, an adjustment or a payment;
 *   * invoices: draft one for a period, then Issue / Mark paid / Void.
 *
 * The confirm pattern of the rest of the console: every write opens a dialog
 * and sends `confirm` = the partner id (api/partner.ts adds it); the server
 * records it in the partner's audit log under the operator's name.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type {
  BillingAccountView,
  BillingModel,
  InvoiceView,
  LedgerEntryView,
  OpsBillingAccountUpdate,
  OpsInvoiceAction,
  OpsLedgerEntryInput,
} from '../api/types';
import { useSession } from '../auth/session';
import { Empty, ErrorNotice, Field, Loading, Modal, Switch, formatDate, formatDateTime, humanize, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { billingError } from './errors';
import { centsToInput, formatBps, formatCents, parseDollars } from './money';
import { Amount, InvoiceStatusTag, LedgerTable, invoiceName, ledgerKindLabel, periodLabel } from './parts';

export const OPS_BILLING_ANCHOR = 'billing';

const DEFAULT_LEAD_FEE_CENTS = 4500;
const DEFAULT_SUCCESS_FEE_BPS = 3000;

export function OpsBillingPanel({ partnerId, displayName }: { partnerId: string; displayName: string }) {
  const { api } = useSession();
  const billing = useQuery(() => api.opsPartnerBilling(partnerId), ['ops-partner-billing', partnerId]);
  const reload = () => void billing.reload();
  const ref = useRef<HTMLElement>(null);
  const loaded = billing.data !== null || billing.error !== null;
  // `/ops/billing` links here as `#billing`: bring the section into view once it has content.
  useEffect(() => {
    if (loaded && window.location.hash === `#${OPS_BILLING_ANCHOR}`) ref.current?.scrollIntoView?.({ block: 'start' });
  }, [loaded]);

  let body: ReactNode;
  if (billing.loading && !billing.data) body = <Loading label="Loading billing" />;
  else if (billing.error?.status === 404) {
    body = (
      <div className="panel-body">
        <p className="small muted" style={{ margin: 0 }}>
          Contractor billing isn’t available for this organisation: the server’s billing switch is off.
        </p>
      </div>
    );
  } else if (billing.error || !billing.data) {
    body = (
      <div className="panel-body">
        <ErrorNotice error={billingError(billing.error)} title="Could not load billing" />
      </div>
    );
  } else {
    const d = billing.data;
    body = (
      <div className="panel-body stack-lg">
        <AccountEditor key={JSON.stringify(d.account)} partnerId={partnerId} displayName={displayName} account={d.account} onSaved={reload} />
        <Entries partnerId={partnerId} displayName={displayName} entries={d.entries} onPosted={reload} />
        <Invoices partnerId={partnerId} displayName={displayName} invoices={d.invoices} onChanged={reload} />
      </div>
    );
  }

  return (
    <section ref={ref} id={OPS_BILLING_ANCHOR} tabIndex={-1} className="panel" style={{ marginTop: 'var(--s5)' }} aria-labelledby="ops-billing-h">
      <div className="panel-head">
        <h2 id="ops-billing-h">Billing</h2>
        {billing.data ? (
          <span className="small muted">
            Balance <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCents(billing.data.balance_cents)}</strong>
          </span>
        ) : null}
      </div>
      {body}
    </section>
  );
}

// ── A confirm dialog that runs one write ────────────────────────────────
function ConfirmWrite({
  title,
  confirmLabel,
  danger,
  run,
  done,
  onClose,
  canSubmit = true,
  children,
}: {
  title: string;
  confirmLabel: string;
  danger?: boolean;
  run: () => Promise<unknown>;
  /** Toast text after it succeeds. */
  done: string;
  onClose: (succeeded: boolean) => void;
  canSubmit?: boolean;
  children: ReactNode;
}) {
  const toast = useToast();
  const action = useMutation(async () => {
    await run();
    return true as const;
  });
  const submit = async () => {
    if (!(await action.run())) return;
    toast(done);
    onClose(true);
  };
  return (
    <Modal
      open
      title={title}
      onClose={() => onClose(false)}
      footer={
        <>
          <button type="button" className="btn" onClick={() => onClose(false)} disabled={action.pending}>
            Cancel
          </button>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={() => void submit()}
            disabled={action.pending || !canSubmit}
          >
            {action.pending ? 'Saving…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="stack-sm">
        {children}
        <p className="small muted" style={{ margin: 0 }}>
          Recorded in the partner’s audit log under your name.
        </p>
        <ErrorNotice error={billingError(action.error)} />
      </div>
    </Modal>
  );
}

// ── Account ──────────────────────────────────────────────────────────────
interface TradeRow {
  trade: string;
  dollars: string;
}

interface AccountForm {
  billing_model: BillingModel;
  lead_fee: string;
  trades: TradeRow[];
  success_fee_bps: string;
  fees_enabled: boolean;
}

function accountForm(a: BillingAccountView | null): AccountForm {
  return {
    billing_model: a?.billing_model ?? 'none',
    lead_fee: centsToInput(a?.lead_fee_cents ?? DEFAULT_LEAD_FEE_CENTS),
    trades: Object.entries(a?.lead_fee_by_trade ?? {}).map(([trade, cents]) => ({ trade, dollars: centsToInput(cents) })),
    success_fee_bps: String(a?.success_fee_bps ?? DEFAULT_SUCCESS_FEE_BPS),
    fees_enabled: a?.fees_enabled ?? false,
  };
}

function validateAccount(f: AccountForm): { update: Omit<OpsBillingAccountUpdate, 'note'> | null; issues: Record<string, string> } {
  const issues: Record<string, string> = {};
  const lead = parseDollars(f.lead_fee);
  if (lead === null || lead < 0) issues.lead_fee = 'Enter a dollar amount, like 45 or 45.00.';
  const bps = Number(f.success_fee_bps);
  if (!/^\d+$/.test(f.success_fee_bps.trim()) || bps > 10000) issues.success_fee_bps = 'Whole basis points from 0 to 10000 (3000 is 30%).';
  const byTrade: Record<string, number> = {};
  f.trades.forEach((row, i) => {
    const trade = row.trade.trim().toLowerCase();
    const cents = parseDollars(row.dollars);
    if (!trade) issues[`trade-${i}`] = 'Name the trade (for example plumbing).';
    else if (trade in byTrade) issues[`trade-${i}`] = 'This trade is listed twice.';
    if (cents === null || cents < 0) issues[`dollars-${i}`] = 'Enter a dollar amount.';
    if (trade && cents !== null && cents >= 0) byTrade[trade] = cents;
  });
  if (Object.keys(issues).length) return { update: null, issues };
  return {
    update: {
      billing_model: f.billing_model,
      lead_fee_cents: lead as number,
      lead_fee_by_trade: byTrade,
      success_fee_bps: bps,
      fees_enabled: f.fees_enabled,
    },
    issues,
  };
}

const issue = (issues: Record<string, string>, key: string) => (issues[key] ? [{ path: key, message: issues[key] as string }] : []);

function AccountEditor({
  partnerId,
  displayName,
  account,
  onSaved,
}: {
  partnerId: string;
  displayName: string;
  account: BillingAccountView | null;
  onSaved: () => void;
}) {
  const { api } = useSession();
  const [form, setForm] = useState<AccountForm>(() => accountForm(account));
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');
  const [showIssues, setShowIssues] = useState(false);
  const noteId = useId();
  const { update, issues } = validateAccount(form);
  const shown = showIssues ? issues : {};
  const dirty = JSON.stringify(form) !== JSON.stringify(accountForm(account));
  const set = (patch: Partial<AccountForm>) => setForm((f) => ({ ...f, ...patch }));
  const setTrade = (i: number, patch: Partial<TradeRow>) =>
    setForm((f) => ({ ...f, trades: f.trades.map((row, j) => (j === i ? { ...row, ...patch } : row)) }));

  return (
    <div className="stack-sm">
      <h3 className="h-sub" style={{ fontSize: 14, margin: 0 }}>
        Account
      </h3>
      {account ? (
        <p className="small muted" style={{ margin: 0 }}>
          {account.billing_model === 'contractor_ppl' ? 'Pay per accepted lead' : 'Not billed'} · fees {account.fees_enabled ? 'on' : 'off'} ·
          enrolled {formatDate(account.enrolled_at)} · fee schedule{' '}
          {account.fee_schedule_version
            ? `${account.fee_schedule_version} accepted ${formatDateTime(account.fee_schedule_accepted_at)}`
            : 'not accepted'}
          {account.billing_email ? ` · invoices to ${account.billing_email}` : ''}
        </p>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          No billing account yet. Saving creates one; nothing bills until fees are on and the partner accepts the fee schedule.
        </p>
      )}
      <form
        className="stack-sm"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setShowIssues(true);
          if (update) setConfirming(true);
        }}
      >
        <div className="grid-2">
          <Field label="Billing model">
            {(p) => (
              <select {...p} className="input" value={form.billing_model} onChange={(e) => set({ billing_model: e.target.value as BillingModel })}>
                <option value="none">Not billed</option>
                <option value="contractor_ppl">Pay per accepted lead</option>
              </select>
            )}
          </Field>
          <Field label="Lead fee ($)" hint="Per accepted lead, unless the trade has its own price." issues={issue(shown, 'lead_fee')}>
            {(p) => <input {...p} className="input num" inputMode="decimal" value={form.lead_fee} onChange={(e) => set({ lead_fee: e.target.value })} />}
          </Field>
          <Field
            label="Success fee (basis points)"
            hint={`On each accepted quote, once the job is confirmed. ${/^\d+$/.test(form.success_fee_bps.trim()) ? `${formatBps(Number(form.success_fee_bps))}.` : ''}`}
            issues={issue(shown, 'success_fee_bps')}
          >
            {(p) => (
              <input {...p} className="input num" inputMode="numeric" value={form.success_fee_bps} onChange={(e) => set({ success_fee_bps: e.target.value })} />
            )}
          </Field>
        </div>
        <Switch
          label="Fees on"
          description="Off: accepted leads and finished jobs post nothing."
          checked={form.fees_enabled}
          onChange={(v) => set({ fees_enabled: v })}
        />
        <fieldset className="stack-sm" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="label" style={{ marginBottom: 4 }}>
            Lead fee by trade
          </legend>
          {form.trades.length ? (
            form.trades.map((row, i) => (
              <div key={i} className="row" style={{ alignItems: 'flex-end' }}>
                <Field label={`Trade ${i + 1}`} issues={issue(shown, `trade-${i}`)}>
                  {(p) => <input {...p} className="input" value={row.trade} placeholder="plumbing" onChange={(e) => setTrade(i, { trade: e.target.value })} />}
                </Field>
                <Field label={`Price for trade ${i + 1} ($)`} issues={issue(shown, `dollars-${i}`)}>
                  {(p) => (
                    <input {...p} className="input num" inputMode="decimal" value={row.dollars} onChange={(e) => setTrade(i, { dollars: e.target.value })} />
                  )}
                </Field>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setForm((f) => ({ ...f, trades: f.trades.filter((_, j) => j !== i) }))}
                >
                  Remove<span className="visually-hidden"> trade {i + 1}</span>
                </button>
              </div>
            ))
          ) : (
            <p className="small muted" style={{ margin: 0 }}>
              Every trade pays the lead fee above.
            </p>
          )}
          <div>
            <button type="button" className="btn btn-sm" onClick={() => setForm((f) => ({ ...f, trades: [...f.trades, { trade: '', dollars: '' }] }))}>
              Add a trade price
            </button>
          </div>
        </fieldset>
        <div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={!dirty && Boolean(account)}>
            Save billing settings…
          </button>
        </div>
      </form>
      {confirming && update ? (
        <ConfirmWrite
          title={`Save billing for ${displayName}?`}
          confirmLabel="Save billing settings"
          canSubmit={note.trim().length > 0}
          run={() => api.opsUpdatePartnerBilling(partnerId, { ...update, note: note.trim() })}
          done={`Billing saved for ${displayName}`}
          onClose={(ok) => {
            setConfirming(false);
            if (ok) {
              setNote('');
              onSaved();
            }
          }}
        >
          <ul className="stack-xs small" style={{ margin: 0, paddingLeft: 18 }}>
            <li>{update.billing_model === 'contractor_ppl' ? 'Pay per accepted lead' : 'Not billed'}</li>
            <li>Lead fee {formatCents(update.lead_fee_cents)}</li>
            {Object.entries(update.lead_fee_by_trade ?? {}).map(([t, c]) => (
              <li key={t}>
                {humanize(t)}: {formatCents(c)}
              </li>
            ))}
            <li>Success fee {formatBps(update.success_fee_bps)}</li>
            <li>Fees {update.fees_enabled ? 'on' : 'off'}</li>
          </ul>
          <p className="small" style={{ margin: 0 }}>
            New prices apply to leads accepted from now on; nothing already posted changes.
          </p>
          <div className="field">
            <label htmlFor={noteId}>Note (required: why)</label>
            <textarea id={noteId} className="input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
          </div>
        </ConfirmWrite>
      ) : null}
    </div>
  );
}

// ── Ledger entries ───────────────────────────────────────────────────────
type EntryKind = OpsLedgerEntryInput['kind'];

interface EntryForm {
  kind: EntryKind;
  amount: string;
  description: string;
  lead_id: string;
  project_id: string;
  reverses: string;
}

const EMPTY_ENTRY: EntryForm = { kind: 'credit', amount: '', description: '', lead_id: '', project_id: '', reverses: '' };

const ENTRY_KIND_HINT: Record<EntryKind, string> = {
  credit: 'Takes the amount off what they owe.',
  payment: 'A payment received outside the portal; takes the amount off what they owe.',
  adjustment: 'Positive adds to what they owe; negative takes it off.',
};

/** The signed cents the form posts: credits and payments are always negative. */
function entryCents(f: EntryForm): number | null {
  const cents = parseDollars(f.amount);
  if (cents === null || cents === 0) return null;
  return f.kind === 'adjustment' ? cents : -Math.abs(cents);
}

function Entries({
  partnerId,
  displayName,
  entries,
  onPosted,
}: {
  partnerId: string;
  displayName: string;
  entries: LedgerEntryView[];
  onPosted: () => void;
}) {
  const { api } = useSession();
  const [form, setForm] = useState<EntryForm>(EMPTY_ENTRY);
  const [confirming, setConfirming] = useState<OpsLedgerEntryInput | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);
  const cents = entryCents(form);
  const description = form.description.trim();
  const shown: Record<string, string> = {};
  if (showIssues && cents === null) shown.amount = 'Enter an amount that isn’t zero, like 45 or 45.00.';
  if (showIssues && !description) shown.description = 'Say what it is for; the partner sees this on their statement.';
  const set = (patch: Partial<EntryForm>) => setForm((f) => ({ ...f, ...patch }));
  const reversing = form.reverses ? entries.find((e) => e.id === form.reverses) : undefined;

  const reverse = (e: LedgerEntryView) => {
    const kind: EntryKind = e.amount_cents > 0 ? 'credit' : 'adjustment';
    setForm({
      kind,
      amount: centsToInput(kind === 'credit' ? Math.abs(e.amount_cents) : -e.amount_cents),
      description: `Reverses ${ledgerKindLabel(e.kind).toLowerCase()} of ${formatDate(e.posted_at)}: ${e.description}`.slice(0, 300),
      lead_id: e.lead_id ?? '',
      project_id: e.project_id ?? '',
      reverses: e.id,
    });
    setShowIssues(false);
    amountRef.current?.focus();
  };

  return (
    <div className="stack-sm">
      <h3 className="h-sub" style={{ fontSize: 14, margin: 0 }}>
        Ledger · last 50
      </h3>
      {entries.length ? (
        <LedgerTable
          entries={entries}
          caption={`Ledger entries for ${displayName}`}
          linkLeads={false}
          actions={(e) => (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => reverse(e)}>
              Reverse<span className="visually-hidden"> {ledgerKindLabel(e.kind)} {formatCents(e.amount_cents)} of {formatDate(e.posted_at)}</span>
            </button>
          )}
        />
      ) : (
        <Empty title="No entries yet" />
      )}
      <form
        className="stack-sm"
        noValidate
        aria-labelledby="ops-billing-entry-h"
        onSubmit={(e) => {
          e.preventDefault();
          setShowIssues(true);
          if (cents === null || !description) return;
          setConfirming({
            kind: form.kind,
            amount_cents: cents,
            description: description.slice(0, 300),
            ...(form.lead_id.trim() ? { lead_id: form.lead_id.trim() } : {}),
            ...(form.project_id.trim() ? { project_id: form.project_id.trim() } : {}),
            ...(form.reverses ? { reverses: form.reverses } : {}),
          });
        }}
      >
        <h4 id="ops-billing-entry-h" style={{ margin: 0, fontSize: 13 }}>
          Add a credit, adjustment or payment
        </h4>
        {reversing ? (
          <div className="notice info small" role="status">
            <span>
              Reversing the {ledgerKindLabel(reversing.kind).toLowerCase()} of {formatCents(reversing.amount_cents)} posted{' '}
              {formatDate(reversing.posted_at)}.{' '}
              <button type="button" className="btn-link" onClick={() => setForm(EMPTY_ENTRY)}>
                Start over
              </button>
            </span>
          </div>
        ) : null}
        <div className="grid-2">
          <Field label="Kind" hint={ENTRY_KIND_HINT[form.kind]}>
            {(p) => (
              <select {...p} className="input" value={form.kind} onChange={(e) => set({ kind: e.target.value as EntryKind })}>
                <option value="credit">Credit</option>
                <option value="adjustment">Adjustment</option>
                <option value="payment">Payment</option>
              </select>
            )}
          </Field>
          <Field
            label="Amount ($)"
            hint={cents !== null ? `Balance change: ${formatCents(cents)}` : undefined}
            issues={shown.amount ? [{ path: 'amount', message: shown.amount }] : []}
          >
            {(p) => (
              <input {...p} ref={amountRef} className="input num" inputMode="decimal" value={form.amount} onChange={(e) => set({ amount: e.target.value })} />
            )}
          </Field>
        </div>
        <Field label="Description" hint="Shown on the partner’s statement." issues={shown.description ? [{ path: 'description', message: shown.description }] : []}>
          {(p) => <input {...p} className="input" maxLength={300} value={form.description} onChange={(e) => set({ description: e.target.value })} />}
        </Field>
        <div className="grid-2">
          <Field label="Lead ID" optional>
            {(p) => <input {...p} className="input input-mono" value={form.lead_id} onChange={(e) => set({ lead_id: e.target.value })} />}
          </Field>
          <Field label="Project ID" optional>
            {(p) => <input {...p} className="input input-mono" value={form.project_id} onChange={(e) => set({ project_id: e.target.value })} />}
          </Field>
        </div>
        <div>
          <button type="submit" className="btn btn-sm">
            Post entry…
          </button>
        </div>
      </form>
      {confirming ? (
        <ConfirmWrite
          title={`Post a ${ledgerKindLabel(confirming.kind).toLowerCase()} for ${displayName}?`}
          confirmLabel="Post entry"
          run={() => api.opsPostBillingEntry(partnerId, confirming)}
          done={`${ledgerKindLabel(confirming.kind)} posted for ${displayName}`}
          onClose={(ok) => {
            setConfirming(null);
            if (ok) {
              setForm(EMPTY_ENTRY);
              setShowIssues(false);
              onPosted();
            }
          }}
        >
          <p style={{ margin: 0 }}>
            <Amount cents={confirming.amount_cents} /> · “{confirming.description}”
          </p>
          <p className="small muted" style={{ margin: 0 }}>
            Ledger entries are permanent: a mistake is fixed with another entry that reverses it.
          </p>
        </ConfirmWrite>
      ) : null}
    </div>
  );
}

// ── Invoices ─────────────────────────────────────────────────────────────
type InvoiceAsk = { action: OpsInvoiceAction; invoice: InvoiceView };

const ACTION_WORDS: Record<OpsInvoiceAction, { button: string; title: (n: string) => string; done: string; danger?: boolean; body: string }> = {
  issue: {
    button: 'Issue',
    title: (n) => `Issue ${n}?`,
    done: 'Invoice issued',
    body: 'It gets its invoice number and a due date (net 15), and the partner sees it as issued. Send it to their billing contact yourself.',
  },
  paid: {
    button: 'Mark paid',
    title: (n) => `Mark ${n} paid?`,
    done: 'Invoice marked paid',
    body: 'Posts a payment for the invoice total to the ledger.',
  },
  void: {
    button: 'Void',
    title: (n) => `Void ${n}?`,
    done: 'Invoice voided',
    danger: true,
    body: 'Its lines go back to uninvoiced; they can be invoiced again.',
  },
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function Invoices({
  partnerId,
  displayName,
  invoices,
  onChanged,
}: {
  partnerId: string;
  displayName: string;
  invoices: InvoiceView[];
  onChanged: () => void;
}) {
  const { api } = useSession();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [ask, setAsk] = useState<InvoiceAsk | null>(null);
  const [paidAt, setPaidAt] = useState(today());
  const [externalRef, setExternalRef] = useState('');
  const paidId = useId();
  const refId = useId();
  const periodOk = Boolean(start && end && start <= end);

  return (
    <div className="stack-sm">
      <h3 className="h-sub" style={{ fontSize: 14, margin: 0 }}>
        Invoices
      </h3>
      {invoices.length ? (
        <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {invoices.map((inv) => (
            <li key={inv.id} className="row-between" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 'var(--s2)' }}>
              <span className="stack-xs" style={{ minWidth: 0 }}>
                <span className="row" style={{ gap: 8 }}>
                  <strong>{invoiceName(inv)}</strong>
                  <InvoiceStatusTag status={inv.status} />
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCents(inv.total_cents)}</span>
                </span>
                <span className="small muted">
                  {periodLabel(inv)}
                  {inv.due_at ? ` · due ${formatDate(inv.due_at)}` : ''}
                  {inv.paid_at ? ` · paid ${formatDate(inv.paid_at)}` : ''}
                </span>
              </span>
              <span className="row" style={{ gap: 6 }}>
                {(inv.status === 'draft' ? (['issue', 'void'] as const) : inv.status === 'issued' ? (['paid', 'void'] as const) : []).map((action) => (
                  <button
                    key={action}
                    type="button"
                    className={action === 'void' ? 'btn btn-danger-subtle btn-sm' : 'btn btn-sm'}
                    onClick={() => {
                      setPaidAt(today());
                      setExternalRef('');
                      setAsk({ action, invoice: inv });
                    }}
                  >
                    {ACTION_WORDS[action].button}
                    <span className="visually-hidden">
                      {' '}
                      {invoiceName(inv)}, {periodLabel(inv)}
                    </span>
                  </button>
                ))}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          No invoices yet.
        </p>
      )}
      <form
        className="row"
        style={{ alignItems: 'flex-end' }}
        noValidate
        aria-label="Draft an invoice"
        onSubmit={(e) => {
          e.preventDefault();
          if (periodOk) setDrafting(true);
        }}
      >
        <Field label="Period start">{(p) => <input {...p} type="date" className="input" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} />}</Field>
        <Field label="Period end">{(p) => <input {...p} type="date" className="input" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />}</Field>
        <button type="submit" className="btn btn-sm" disabled={!periodOk}>
          Draft invoice…
        </button>
      </form>
      {drafting ? (
        <ConfirmWrite
          title={`Draft an invoice for ${displayName}?`}
          confirmLabel="Draft invoice"
          run={() => api.opsDraftInvoice(partnerId, start, end)}
          done={`Invoice drafted for ${displayName}`}
          onClose={(ok) => {
            setDrafting(false);
            if (ok) {
              setStart('');
              setEnd('');
              onChanged();
            }
          }}
        >
          <p style={{ margin: 0 }}>
            Gathers every uninvoiced entry posted from {formatDate(start)} to {formatDate(end)} into a draft. Nothing is sent until you issue
            it.
          </p>
        </ConfirmWrite>
      ) : null}
      {ask ? (
        <ConfirmWrite
          title={ACTION_WORDS[ask.action].title(invoiceName(ask.invoice))}
          confirmLabel={ACTION_WORDS[ask.action].button}
          danger={ACTION_WORDS[ask.action].danger}
          canSubmit={ask.action !== 'paid' || Boolean(paidAt)}
          run={() =>
            api.opsInvoiceAction(partnerId, ask.invoice.id, ask.action, ask.action === 'paid' ? { paid_at: paidAt, external_ref: externalRef } : {})
          }
          done={ACTION_WORDS[ask.action].done}
          onClose={(ok) => {
            setAsk(null);
            if (ok) onChanged();
          }}
        >
          <p style={{ margin: 0 }}>
            {formatCents(ask.invoice.total_cents)} · {periodLabel(ask.invoice)}. {ACTION_WORDS[ask.action].body}
          </p>
          {ask.action === 'paid' ? (
            <>
              <div className="field">
                <label htmlFor={paidId}>Paid on</label>
                <input id={paidId} type="date" className="input" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor={refId}>
                  Payment reference<span className="faint" style={{ fontWeight: 500 }}> · optional</span>
                </label>
                <input id={refId} className="input input-mono" maxLength={200} value={externalRef} onChange={(e) => setExternalRef(e.target.value)} />
              </div>
            </>
          ) : null}
        </ConfirmWrite>
      ) : null}
    </div>
  );
}
